import { useState, useEffect, useCallback } from 'react';
import {
  HomeExpenseDto,
  HomeExpenseGroupDto,
  CreateHomeExpenseCommand,
  HomeExpenseFields,
  CreateMultipleHomeExpensesCommand,
  HomeExpenseSummaryDto,
  AccountDto,
  CreateAccountCommand,
  UpdateAccountCommand,
  ExpenseType,
  CategoryMonthStatDto,
} from '../types/homeExpenses';
import * as homeExpensesApi from '../services/homeExpensesApi';

// Custom hooks for Home Expenses management

/**
 * Hook for managing home expenses list with loading and error states
 */
export const useHomeExpenses = (type?: ExpenseType) => {
  const [expenses, setExpenses] = useState<HomeExpenseDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await homeExpensesApi.getHomeExpenses(type);
      setExpenses(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível carregar as despesas');
    } finally {
      setLoading(false);
    }
  }, [type]);

  const createExpense = useCallback(async (command: CreateHomeExpenseCommand) => {
    setLoading(true);
    setError(null);
    try {
      const newExpense = await homeExpensesApi.createHomeExpense(command);
      setExpenses(prev => [newExpense, ...prev]);
      return newExpense;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível criar a despesa');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createMultipleExpenses = useCallback(async (command: CreateMultipleHomeExpensesCommand) => {
    setLoading(true);
    setError(null);
    try {
      const newExpenses = await homeExpensesApi.createMultipleHomeExpenses(command);
      setExpenses(prev => [...newExpenses, ...prev]);
      return newExpenses;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível criar as despesas');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateExpense = useCallback(async (publicId: string, command: HomeExpenseFields) => {
    setLoading(true);
    setError(null);
    try {
      const updatedExpense = await homeExpensesApi.updateHomeExpense(publicId, command);
      setExpenses(prev => prev.map(expense => 
        expense.publicId === publicId ? updatedExpense : expense
      ));
      return updatedExpense;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível atualizar a despesa');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteExpense = useCallback(async (publicId: string) => {
    setLoading(true);
    setError(null);
    try {
      await homeExpensesApi.deleteHomeExpense(publicId);
      setExpenses(prev => prev.filter(expense => expense.publicId !== publicId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível eliminar a despesa');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  return {
    expenses,
    loading,
    error,
    refresh: fetchExpenses,
    create: createExpense,
    createMultiple: createMultipleExpenses,
    update: updateExpense,
    delete: deleteExpense,
  };
};

/**
 * Hook for managing home expense groups
 */
export const useHomeExpenseGroups = (type?: ExpenseType) => {
  const [groups, setGroups] = useState<HomeExpenseGroupDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await homeExpensesApi.getHomeExpenseGroups(type);
      setGroups(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível carregar os grupos');
    } finally {
      setLoading(false);
    }
  }, [type]);

  const createGroup = useCallback(async (
    name: string, 
    description?: string, 
    isIncomeGroup: boolean = false,
    color?: string
  ) => {
    setLoading(true);
    setError(null);
    try {
      const newGroup = await homeExpensesApi.createHomeExpenseGroup(name, description, isIncomeGroup, color);
      setGroups(prev => [...prev, newGroup]);
      return newGroup;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível criar o grupo');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  return {
    groups,
    loading,
    error,
    refresh: fetchGroups,
    create: createGroup,
  };
};

/**
 * Hook for managing accounts
 */
export const useAccounts = (type?: ExpenseType) => {
  const [accounts, setAccounts] = useState<AccountDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await homeExpensesApi.getAccounts(type);
      setAccounts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível carregar as contas');
    } finally {
      setLoading(false);
    }
  }, [type]);

  const createAccount = useCallback(async (command: CreateAccountCommand) => {
    setLoading(true);
    setError(null);
    try {
      const newAccount = await homeExpensesApi.createAccount(
        command.name,
        command.description,
        command.color,
        command.isPrimary
      );
      setAccounts(prev => [...prev, newAccount]);
      return newAccount;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível criar a conta');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateAccount = useCallback(async (publicId: string, command: UpdateAccountCommand) => {
    setLoading(true);
    setError(null);
    try {
      const updatedAccount = await homeExpensesApi.updateAccount(
        publicId,
        command.name,
        command.description,
        command.color,
        command.isPrimary
      );
      setAccounts(prev => prev.map(account => 
        account.publicId === publicId ? updatedAccount : account
      ));
      return updatedAccount;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível atualizar a conta');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteAccount = useCallback(async (publicId: string) => {
    setLoading(true);
    setError(null);
    try {
      await homeExpensesApi.deleteAccount(publicId);
      setAccounts(prev => prev.filter(account => account.publicId !== publicId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível eliminar a conta');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  return {
    accounts,
    loading,
    error,
    refresh: fetchAccounts,
    create: createAccount,
    update: updateAccount,
    delete: deleteAccount,
  };
};

/**
 * Hook for fetching expense summary statistics
 */
export const useHomeExpensesSummary = (type?: ExpenseType, startDate?: string, endDate?: string) => {
  const [summary, setSummary] = useState<HomeExpenseSummaryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async (start?: string, end?: string, t?: ExpenseType) => {
    setLoading(true);
    setError(null);
    try {
      const data = await homeExpensesApi.getHomeExpensesSummary(start, end, t);
      setSummary(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível carregar o resumo das despesas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary(startDate, endDate, type);
  }, [startDate, endDate, type, fetchSummary]);

  return {
    summary,
    loading,
    error,
    refresh: () => fetchSummary(startDate, endDate),
  };
};

/**
 * Hook for CSV import functionality
 */
export const useHomeExpensesCsvImport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastImported, setLastImported] = useState<HomeExpenseDto[]>([]);

  const importFromCsv = useCallback(async (file: File, accountPublicId: string) => {
    setLoading(true);
    setError(null);
    try {
      const importedExpenses = await homeExpensesApi.importHomeExpensesFromCsv(file, accountPublicId);
      setLastImported(importedExpenses);
      return importedExpenses;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível importar o CSV');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    lastImported,
    importFromCsv,
  };
};

/**
 * Hook for fetching Category vs Month breakdown analytics
 */
export const useCategoryMonthBreakdown = (year?: number, type?: ExpenseType) => {
  const [data, setData] = useState<CategoryMonthStatDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (y?: number, t?: ExpenseType) => {
    setLoading(true);
    setError(null);
    try {
      const res = await homeExpensesApi.getCategoryMonthBreakdown(y, t);
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível carregar a análise');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(year, type);
  }, [year, type, fetchData]);

  return { data, loading, error, refresh: () => fetchData(year, type) };
};
